/**
 * API client for the Takeovers dashboard (takeover requests + creators). All
 * routes are admin-authenticated; permission gates live server-side
 * (takeovers.view/manage). Creator report dashboards: creatorDashboardsApi.ts.
 */

import { apiRequest, API_BASE_URL, getAuthToken } from './api';
import {
  OverlappingApproved,
  TakeoverAnalyticsSummary,
  TakeoverChannelCandidate,
  TakeoverChannelDetails,
  TakeoverCreator,
  TakeoverCreatorYouTube,
  TakeoverRequest,
  TakeoverRequestStatus,
} from '../components/Takeovers/types';

const TAKEOVERS = '/api/admin/takeovers';

// --- Creators ----------------------------------------------------------------

export async function listTakeoverCreators(): Promise<TakeoverCreator[]> {
  const body = await apiRequest(`${TAKEOVERS}/creators`);
  return body.creators ?? [];
}

export interface CreatorInput {
  displayName: string;
  slug?: string;
  avatarUrl?: string;
  bannerUrl?: string;
  blurb?: string;
  subsDisplay?: string;
  /** null clears the linked channel (PATCH). */
  youtube?: TakeoverCreatorYouTube | null;
  active?: boolean;
  contacts?: Array<{ name: string; email: string; notify: boolean }>;
  budgetTiers?: Array<{ id: string; label: string; minUsd?: number; maxUsd?: number; sortOrder: number }>;
}

/** Channel picker search — CRM ("Network") + YouTube candidates. */
export async function searchTakeoverChannels(
  q: string,
): Promise<{ candidates: TakeoverChannelCandidate[]; youtubeError?: string }> {
  const body = await apiRequest(`${TAKEOVERS}/channel-search?q=${encodeURIComponent(q)}`);
  return { candidates: body.candidates ?? [], youtubeError: body.youtubeError };
}

/** Fresh channel identity (title, handle, subs, avatar, banner) for auto-fill. */
export async function lookupTakeoverChannel(channelId: string): Promise<TakeoverChannelDetails> {
  const body = await apiRequest(`${TAKEOVERS}/channel-lookup/${encodeURIComponent(channelId)}`);
  return body.channel;
}

export async function createTakeoverCreator(input: CreatorInput): Promise<TakeoverCreator> {
  const body = await apiRequest(`${TAKEOVERS}/creators`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return body.creator;
}

export async function updateTakeoverCreator(id: string, patch: Partial<CreatorInput>): Promise<TakeoverCreator> {
  const body = await apiRequest(`${TAKEOVERS}/creators/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return body.creator;
}

export async function regenerateTakeoverSlug(id: string): Promise<TakeoverCreator> {
  const body = await apiRequest(`${TAKEOVERS}/creators/${encodeURIComponent(id)}/regenerate-slug`, {
    method: 'POST',
  });
  return body.creator;
}

// --- Requests ----------------------------------------------------------------

export interface RequestFilters {
  creatorId?: string;
  status?: TakeoverRequestStatus;
  budgetTierId?: string;
  createdFrom?: string;
  createdTo?: string;
  limit?: number;
  cursor?: string;
}

export async function listTakeoverRequests(
  filters: RequestFilters = {},
): Promise<{ requests: TakeoverRequest[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value));
  });
  const qs = params.toString();
  return apiRequest(`${TAKEOVERS}/requests${qs ? `?${qs}` : ''}`);
}

export async function getTakeoverRequest(
  id: string,
): Promise<{ request: TakeoverRequest; overlappingApproved: OverlappingApproved[] }> {
  return apiRequest(`${TAKEOVERS}/requests/${encodeURIComponent(id)}`);
}

export async function changeTakeoverStatus(
  id: string,
  to: TakeoverRequestStatus,
  note?: string,
): Promise<TakeoverRequest> {
  const body = await apiRequest(`${TAKEOVERS}/requests/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ to, ...(note ? { note } : {}) }),
  });
  return body.request;
}

export async function addTakeoverNote(id: string, text: string): Promise<TakeoverRequest> {
  const body = await apiRequest(`${TAKEOVERS}/requests/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  return body.request;
}

export async function resendTakeoverNotifications(id: string): Promise<void> {
  await apiRequest(`${TAKEOVERS}/requests/${encodeURIComponent(id)}/resend-notifications`, {
    method: 'POST',
  });
}

export async function getTakeoverAnalytics(): Promise<TakeoverAnalyticsSummary> {
  const body = await apiRequest(`${TAKEOVERS}/analytics/summary`);
  return body.summary;
}

/** Authenticated CSV download (fetch + blob — the endpoint needs the ID token header). */
export async function downloadTakeoverCsv(filters: { creatorId?: string; status?: string } = {}): Promise<void> {
  const params = new URLSearchParams();
  if (filters.creatorId) params.set('creatorId', filters.creatorId);
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE_URL}${TAKEOVERS}/requests/export.csv${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'takeover-requests.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
